import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Modal } from 'antd';
import { SbSocketErrorCode } from 'sb-lib/sb-socket-abstract';
import { useUpdatingRef } from 'shared/hooks';
import { useAppSelector } from 'shared/store';
import { makeLog } from 'shared/utils/log';
import { connectServer } from 'shared/store/realtime-slice';
import { Button } from '../Button';
import { Icon } from '../Icon';
import PauseIcon from './pause.svg';
import WarningIcon from './warning.svg';
import './style.less';

const log = makeLog('SocketKickModal');

const configs = {
  [SbSocketErrorCode.EXISTING_USER_SOCKET_BUSY]: {
    icon: <WarningIcon className="SocketKickModal__icon" />,
    title: <>You are on a call in another session</>,
    text: <>Continuing here will end the call</>,
    button: {
      className: 'SocketKickModal__cta SocketKickModal__cta--busy',
      icon: <Icon icon="phoneOff" />,
    },
    buttonText: <>End call & continue</>,
    connectServerArgs: {
      kickOther: true,
      passive: false,
      reason: 'agent decided to kick other active session',
    },
  },
  [SbSocketErrorCode.PASSIVE_REJECTION]: {
    icon: <PauseIcon className="SocketKickModal__icon" />,
    title: <>Paused due to other active session</>,
    text: <>Continuing here will pause other session</>,
    button: {
      className: 'SocketKickModal__cta',
      icon: <Icon icon="playOutline" />,
    },
    buttonText: <>Continue session</>,
    connectServerArgs: {
      kickOther: false,
      passive: false,
      reason: 'agent decided to kick other session',
    },
  },
  [SbSocketErrorCode.DISPLACED]: {
    icon: <PauseIcon className="SocketKickModal__icon" />,
    title: <>Paused due to other active session</>,
    text: <>Continuing here will pause other sessions</>,
    button: {
      className: 'SocketKickModal__cta',
      icon: <Icon icon="playOutline" />,
    },
    buttonText: <>Continue session</>,
    connectServerArgs: {
      kickOther: false,
      passive: false,
      reason: 'agent decided to kick other session',
    },
  },
  OTHER: {
    icon: <PauseIcon className="SocketKickModal__icon" />,
    title: <>Connection paused</>,
    text: <>Continuing here will pause other sessions</>,
    button: {
      className: 'SocketKickModal__cta',
      icon: <Icon icon="playOutline" />,
    },
    buttonText: <>Continue session</>,
    connectServerArgs: {
      kickOther: false,
      passive: false,
      reason: 'agent wants to connect',
    },
  },
};

const isConfigKey = (name: string): name is keyof typeof configs => {
  return Object.keys(configs).includes(name);
};
const getConfig = (name: string) => {
  if (isConfigKey(name)) {
    return configs[name];
  }
  return configs.OTHER;
};

interface Props {
  centered?: boolean;
}

export const SocketKickModal: React.FC<Props> = ({ centered }) => {
  const dispatch = useDispatch();
  const { serverErrorCode, serverStatus } = useAppSelector(s => s.realtime);
  const [isShowPassiveModal, setIsShowPassiveModal] = useState(false);

  const isSelfOtherSocketBusy =
    serverErrorCode === SbSocketErrorCode.EXISTING_USER_SOCKET_BUSY;

  const isSelfOtherSocketConnected =
    serverErrorCode === SbSocketErrorCode.PASSIVE_REJECTION;

  const isSelfDisconnected = serverErrorCode === SbSocketErrorCode.DISPLACED;

  const isModalVisible =
    isSelfOtherSocketBusy || isSelfOtherSocketConnected || isSelfDisconnected;

  const isPassivelyDisconnected = serverStatus === 'disconnected' && !isModalVisible;
  const isPassivelyDisconnectedRef = useUpdatingRef(isPassivelyDisconnected);

  const config = getConfig(serverErrorCode || 'OTHER');

  useEffect(() => {
    let toId = 0;
    if (isPassivelyDisconnected) {
      log.debug(
        "'Passively' disconnected, will show modal if still disconnected in 5 seconds...",
      );
      toId = window.setTimeout(() => {
        if (isPassivelyDisconnectedRef.current) {
          log.debug('Still passively disconnected after 5 seconds, show modal.');
          setIsShowPassiveModal(true);
        }
      }, 5000);
    }
    return () => clearTimeout(toId);
  }, [isPassivelyDisconnected, isPassivelyDisconnectedRef]);

  useEffect(() => {
    // Kill the modal if it's set open for passively disconnected
    if (isShowPassiveModal && !isPassivelyDisconnected) {
      setIsShowPassiveModal(false);
    }
  }, [isShowPassiveModal, isPassivelyDisconnected]);

  return (
    <Modal
      maskStyle={
        {
          background: 'rgba(144, 144, 173, 0.57)',
          backdropFilter: 'blur(4px)',
          '-webkit-backdrop-filter': 'blur(4px)',
        } as any
      }
      className="SocketKickModal"
      visible={isModalVisible || isShowPassiveModal}
      closable={false}
      centered={centered}
      cancelButtonProps={{ style: { display: 'none' } }}
    >
      {config.icon}
      <div className="SocketKickModal__title">{config.title}</div>
      <div className="SocketKickModal__subtitle">{config.text}</div>
      <Button
        {...config.button}
        type="primary"
        onClick={() => {
          dispatch(connectServer(config.connectServerArgs));
          setIsShowPassiveModal(false);
        }}
      >
        {config.buttonText}
      </Button>
    </Modal>
  );
};
